import get from 'lodash/get'
import reduce from 'lodash/reduce'
import set from 'lodash/set'
import { useEffect, useState, useRef } from 'react'
import Modal from 'react-modal'
import styled from 'styled-components'
import useWorksCount from '@pc/components/Works/Panel/hooks/useWorksCount'
import SearchControlBox from '@pc/components/Works/Panel/shared/SearchControlBox'
import type { SearchParamsAttributes, TimeSlot, FeatureCategory, Feature, FilterCondition } from '@pc/components/Works/Panel/types'
import convertFeatureParams from '@pc/components/Works/Panel/utils/convertFeatureParams'
import { buildDynamicSearchApiUrl } from '@pc/components/Works/Panel/utils/workSearchApi'
import DateTimeBox from './FeatureSelectModal/DateTimeBox'
import FeatureList from './FeatureSelectModal/FeatureList'
import FeaturePanelButton from './FeatureSelectModal/FeaturePanelButton'
import FilterConditionBox from './FeatureSelectModal/FilterConditionBox'
import FreewordBox from './FeatureSelectModal/FreewordBox'
import PanelButtonPopupIcon from './PanelButtonPopupIcon'
import ModalCloseButton from './shared/ModalCloseButton'
import ModalFooter from './shared/ModalFooter'
import ModalHeader from './shared/ModalHeader'
import ModalInner from './shared/ModalInner'
import modalStyles from './shared/modalStyles'
import ModalTitle from './shared/ModalTitle'
import type { ComponentType, MouseEvent } from 'react'

interface StateType {
  freeWord: SearchParamsAttributes['free_word']
  selectedKeepUpDay: SearchParamsAttributes['keepup_day']
  selectedKeepUpHour: SearchParamsAttributes['keepup_hour']
  selectedTimeSlotIds: SearchParamsAttributes['time_slots']
  selectedFeatureIds: string[]
  filterConditions: FilterCondition
}

interface PropsType {
  params: SearchParamsAttributes
  timeSlots: TimeSlot[]
  featureCategories: FeatureCategory[]
  features: Feature[]
}

type FeatureParams = Record<string, string[] | null>

const FeatureContentWrapper = styled.div`
  bottom: 85px;
  left: 0;
  overflow-y: scroll;
  padding: 0 20px;
  position: absolute;
  top: 68px;
  width: 100%;
`

const ReactModal = Modal as unknown as ComponentType<any>

const el = document.querySelector<HTMLElement>('.js-works-search-panel')
if (el !== null) {
  Modal.setAppElement('.js-works-search-panel')
}

const FeatureSelectModal = (props: PropsType) => {
  const { params: paramsAttr, timeSlots, featureCategories, features } = props

  // descriptionテーブルに基づいた検索リストのパラメータを新特徴マスタの値に変換するメソッド
  // TODO: 検索リストのurlが新特徴マスタのモノに移行したら消す
  const translateFeaturesFromParams = () => {
    const { features, custom_codes: customCodes, welcome, working_time: workingTime } = paramsAttr
    const params = convertFeatureParams(features, customCodes, welcome, workingTime)
    return params.map((featureId) => featureId.toString())
  }

  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [state, setState] = useState<StateType>({
    freeWord: paramsAttr.free_word ?? '',
    selectedKeepUpDay: paramsAttr.keepup_day,
    selectedKeepUpHour: paramsAttr.keepup_hour,
    selectedTimeSlotIds: paramsAttr.time_slots ?? [],
    selectedFeatureIds: translateFeaturesFromParams(),
    filterConditions: {
      isAcceptHighschoolStudents: paramsAttr.accept_highschool_students !== null,
      isAcceptStudents: paramsAttr.accept_students !== null,
      isExceptRightHighschoolEducation: paramsAttr.except_right_highschool_education !== null,
      isExceptRightCollegeEducation: paramsAttr.except_right_college_education !== null,
      isExceptHaken: paramsAttr.except_haken !== null,
      isExceptMedical: paramsAttr.except_medical_services !== null,
      isNewArrival: paramsAttr.custom_codes?.includes('newarrival') ?? false // 新着はURL上ではspecialだがパネルでは表示条件の括りになっている
    }
  })
  const { worksCount, updateCountFromParams } = useWorksCount()
  const renderFlgRef = useRef(false)

  // 現状の検索の実装ではwelcomeが複数値を受け取れないので表示しないようにしている
  const featuresExceptWelcome = features.filter((feature) => feature.attributes.legacy_url_key !== 'welcome')
  const mappedFeatures = featureCategories.reduce((result, featureCategory) => {
    const value = featuresExceptWelcome.filter((feature) => feature.attributes.feature_category_id === featureCategory.attributes.id)
    return { ...result, [featureCategory.id]: value }
  }, {})

  const toggleModal = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setModalIsOpen(!modalIsOpen)
  }

  const changeState = (newState: StateType) => {
    setState({ ...state, ...newState })
  }

  const clearState = () => {
    setState({
      ...state,
      freeWord: '',
      selectedKeepUpDay: null,
      selectedKeepUpHour: null,
      selectedTimeSlotIds: [],
      selectedFeatureIds: [],
      filterConditions: {
        isNewArrival: false,
        isExceptHaken: false,
        isExceptMedical: false,
        isAcceptHighschoolStudents: false,
        isAcceptStudents: false,
        isExceptRightHighschoolEducation: false,
        isExceptRightCollegeEducation: false
      }
    })
  }

  // 新特徴マスタの値をDescriptionテーブルに基づいた検索リストURLの値に変換するメソッド
  // TODO: 検索リストのURLが新特徴マスタのモノに移行したら消す
  const translateLegacyDescription = (featureIds: string[]) => {
    const selectedFeatures: Feature[] = features.filter((feature) => {
      return featureIds.includes(feature.id)
    })

    return reduce(
      selectedFeatures,
      (result, feature) => {
        const prevData: string[] = get(result, feature.attributes.legacy_url_key, [])
        set(result, feature.attributes.legacy_url_key, prevData.concat(feature.attributes.legacy_url_value))
        return result
      },
      { features: [], custom_codes: [], welcome: [], working_time: [] }
    )
  }

  const mergedParams = () => {
    const { freeWord, selectedFeatureIds, selectedKeepUpDay, selectedKeepUpHour, selectedTimeSlotIds, filterConditions } = state

    let featureParams: FeatureParams = translateLegacyDescription(selectedFeatureIds)

    // 検索パネルの仕様上、welcomeを保持したままにしていると検索結果がおかしくなってしまうためリセットしている
    featureParams = { ...featureParams, welcome: null }

    if (filterConditions.isNewArrival as boolean) {
      featureParams.custom_codes = featureParams.custom_codes?.concat('newarrival') ?? []
    }

    // 以下の検索パラメータは有効にする場合は1、無効にする場合はnullでないと404になる
    const filterConditionParams = {
      accept_highschool_students: (filterConditions.isAcceptHighschoolStudents as boolean) ? 1 : null,
      accept_students: (filterConditions.isAcceptStudents as boolean) ? 1 : null,
      except_right_highschool_education: (filterConditions.isExceptRightHighschoolEducation as boolean) ? 1 : null,
      except_right_college_education: (filterConditions.isExceptRightCollegeEducation as boolean) ? 1 : null,
      except_medical_services: (filterConditions.isExceptMedical as boolean) ? 1 : null,
      except_haken: (filterConditions.isExceptHaken as boolean) ? 1 : null
    }

    const timeConditions = {
      time_slots: selectedTimeSlotIds,
      keepup_day: selectedKeepUpDay,
      keepup_hour: selectedKeepUpHour
    }

    return {
      ...paramsAttr,
      ...filterConditionParams,
      ...featureParams,
      ...timeConditions,
      free_word: freeWord
    }
  }

  useEffect(() => {
    if (renderFlgRef.current) {
      const { freeWord, selectedFeatureIds, selectedKeepUpDay, selectedKeepUpHour, selectedTimeSlotIds, filterConditions } = state

      // translateLegacyDescription inline
      const selectedFeatures: Feature[] = features.filter((feature) => {
        return selectedFeatureIds.includes(feature.id)
      })
      let featureParams: FeatureParams = reduce(
        selectedFeatures,
        (result, feature) => {
          const prevData: string[] = get(result, feature.attributes.legacy_url_key, [])
          set(result, feature.attributes.legacy_url_key, prevData.concat(feature.attributes.legacy_url_value))
          return result
        },
        { features: [], custom_codes: [], welcome: [], working_time: [] }
      )

      featureParams = { ...featureParams, welcome: null }

      if (filterConditions.isNewArrival as boolean) {
        featureParams.custom_codes = featureParams.custom_codes?.concat('newarrival') ?? []
      }

      const filterConditionParams = {
        accept_highschool_students: (filterConditions.isAcceptHighschoolStudents as boolean) ? 1 : null,
        accept_students: (filterConditions.isAcceptStudents as boolean) ? 1 : null,
        except_right_highschool_education: (filterConditions.isExceptRightHighschoolEducation as boolean) ? 1 : null,
        except_right_college_education: (filterConditions.isExceptRightCollegeEducation as boolean) ? 1 : null,
        except_medical_services: (filterConditions.isExceptMedical as boolean) ? 1 : null,
        except_haken: (filterConditions.isExceptHaken as boolean) ? 1 : null
      }

      const timeConditions = {
        time_slots: selectedTimeSlotIds,
        keepup_day: selectedKeepUpDay,
        keepup_hour: selectedKeepUpHour
      }

      const mergedParams = {
        ...paramsAttr,
        ...filterConditionParams,
        ...featureParams,
        ...timeConditions,
        free_word: freeWord
      }
      updateCountFromParams(mergedParams)
    } else {
      renderFlgRef.current = true
    }
  }, [state, paramsAttr, features, updateCountFromParams])

  return (
    <>
      <FeaturePanelButton onClick={toggleModal}>
        さらに詳しい条件を指定する
        <PanelButtonPopupIcon />
      </FeaturePanelButton>
      <ReactModal isOpen={modalIsOpen} onRequestClose={toggleModal} style={modalStyles}>
        <ModalInner>
          <ModalCloseButton onClick={toggleModal} />
          <ModalHeader>
            <ModalTitle>こだわり条件で検索</ModalTitle>
          </ModalHeader>
          <FeatureContentWrapper>
            <FreewordBox freeWord={state.freeWord} changeState={changeState} />
            <DateTimeBox
              timeSlots={timeSlots}
              selectedTimeSlotIds={state.selectedTimeSlotIds}
              selectedKeepUpDay={state.selectedKeepUpDay}
              selectedKeepUpHour={state.selectedKeepUpHour}
              changeState={changeState}
            />
            <FeatureList
              mappedFeatures={mappedFeatures}
              featureCategories={featureCategories}
              selectedFeatureIds={state.selectedFeatureIds}
              changeState={changeState}
            />
            <FilterConditionBox filterConditions={state.filterConditions} changeState={changeState} />
          </FeatureContentWrapper>
          <ModalFooter>
            <SearchControlBox searchUrl={buildDynamicSearchApiUrl(mergedParams())} onClickClearButton={clearState} worksCount={worksCount} />
          </ModalFooter>
        </ModalInner>
      </ReactModal>
    </>
  )
}

export default FeatureSelectModal
